import classNames from 'classnames';
import { defineMessages, FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import PrivacyTrigger from '@alltrails/shared/components/PrivacyTrigger';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import TextInput from '@alltrails/shared/denali/components/TextInput';
import Select from '@alltrails/shared/denali/components/Select';
import TextArea from '@alltrails/shared/denali/components/TextArea';
import useActivityStrings from '@alltrails/shared/hooks/useActivityStrings';
import DatePicker from '@alltrails/shared/components/DatePicker';
import StarRating from '../../shared/StarRating';
import * as styles from './styles/styles.module.scss';
import { visibilityToTranslatedOption } from '../../../utils/privacy_policy_helpers';

const TRACK_UPLOAD_FIELDS_STRINGS = defineMessages({
  RATING: { defaultMessage: 'Rating' },
  NAME: { defaultMessage: 'Name (optional)' },
  NOTES: { defaultMessage: 'Notes (optional)' },
  TYPE: { defaultMessage: 'Activity type' },
  DATE: { defaultMessage: 'Date' }
});

export type Props = {
  updateTrackForm: (value: any) => void;
  handlePrivacyTriggerClick: () => void;
  name?: string;
  description?: string;
  activity: {
    value: string;
    label: string;
  };
  rating: number;
  className?: string;
  date: Date;
  contentPrivacy?: string;
  editMode?: boolean;
};

const TrackUploadFields = ({
  updateTrackForm,
  handlePrivacyTriggerClick,
  name,
  description,
  rating,
  activity,
  className,
  date,
  contentPrivacy,
  editMode
}: Props): JSX.Element => {
  const { activitySelectOptions, findByValue } = useActivityStrings();

  const {
    formattedDefaultMessages: { RATING, NAME, NOTES, TYPE, DATE }
  } = useFormatMessage(TRACK_UPLOAD_FIELDS_STRINGS);
  const intl = useIntl();

  const handleTextChange = (value: string, type: string) => {
    updateTrackForm({ [type]: value });
  };

  const handleActivityChange = (value: string) => {
    updateTrackForm({ activity: findByValue(value) });
  };

  const handleDateChange = (newDate: Date) => {
    updateTrackForm({ date: newDate });
  };

  const handleRatingChange = (e: React.ChangeEvent<HTMLInputElement>, updateRating: number) => {
    e.preventDefault();
    updateTrackForm({ rating: updateRating });
  };

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.formSection}>
        {!editMode && (
          <div className={styles.starRatingContainer}>
            <div className={styles.labelName}>{RATING}</div>
            <div className={styles.stars}>
              <StarRating handler={handleRatingChange} config={rating} iconClassName={styles.starRatingIconClass} />
            </div>
          </div>
        )}
        <TextInput
          type="text"
          testId="activity_edit_name"
          labelText={NAME}
          value={name}
          onChange={value => handleTextChange(value, 'name')}
          className={styles.nameAndDescription}
        />
      </div>
      <div className={styles.typeAndDate}>
        <div className={styles.formSection}>
          <Select
            labelText={TYPE}
            placeholder=""
            options={activitySelectOptions}
            onChange={value => handleActivityChange(value)}
            value={activity.value}
            testId="activity-upload-type-select"
          />
        </div>
        <div className={styles.formSection}>
          <div className={styles.labelName}>{DATE}</div>
          <DatePicker date={date} onChange={handleDateChange} materialRootClass={styles.datePicker} />
        </div>
      </div>
      <div className={styles.formSection}>
        <TextArea
          labelText={NOTES}
          value={description}
          onChange={value => handleTextChange(value, 'description')}
          testId="activity_edit_notes"
          rows={3}
          className={styles.nameAndNotes}
        />
      </div>
      <div className={classNames(styles.formSection, styles.privacyContainer)}>
        <PrivacyTrigger
          onClick={handlePrivacyTriggerClick}
          label={<FormattedMessage defaultMessage="Privacy" />}
          currentOption={visibilityToTranslatedOption(contentPrivacy, intl)}
          size="md"
        />
      </div>
    </div>
  );
};

export default TrackUploadFields;
